import { useEffect } from "react";
import MaconHeroSection from "./components/HeroSection";
import MaconStickyNav from "./components/StickyNav";
import MaconProcessingTimeSection from "./components/ProcessingTimeSection";
import MaconServicesSection from "./components/ServicesSection";
import MaconLocalAreaSection from "./components/LocalAreaSection";
import MaconTestimonialsSection from "./components/TestimonialsSection";
import MaconAcceptanceFacilitiesSection from "./components/AcceptanceFacilitiesSection";
import MaconComparisonSection from "./components/ComparisonSection";
import MaconMistakesSection from "./components/MistakesSection";
import MaconHowItWorksSection from "./components/HowItWorksSection";
import MaconFAQSection, { faqs } from "./components/FAQSection";
import MaconHelpfulResourcesSection from "./components/HelpfulResourcesSection";
import MaconFinalCTA from "./components/FinalCTA";
import SEOCrossLinks from "@/components/feature/SEOCrossLinks";

export default function MaconPage() {
  useEffect(() => {
    document.title = "Emergency & Expedited Passport Macon GA | Same-Day Service | Fast Passport Center";

    const setMeta = (name: string, content: string) => {
      let el = document.querySelector(`meta[name="${name}"]`);
      if (!el) {
        el = document.createElement("meta");
        el.setAttribute("name", name);
        document.head.appendChild(el);
      }
      el.setAttribute("content", content);
    };
    const setLink = (rel: string, href: string) => {
      let el = document.querySelector(`link[rel="${rel}"]`);
      if (!el) {
        el = document.createElement("link");
        el.setAttribute("rel", rel);
        document.head.appendChild(el);
      }
      el.setAttribute("href", href);
    };

    const pageUrl = "https://fastpassportcenter.com/macon/";

    setLink("canonical", pageUrl);
    setMeta("description", "Emergency and expedited passport services in Macon, GA — as fast as 24 hours. Same-day courier processing available. A+ BBB rated, registered State Dept. courier serving Middle Georgia travelers. Get your emergency passport fast!");
    setMeta("geo.placename", "Macon, Georgia");
    setMeta("geo.region", "US-GA");
    setMeta("geo.position", "32.8407;-83.6324");
    setMeta("ICBM", "32.8407, -83.6324");
    setMeta("keywords", "expedited passport, emergency passport, Macon passport, same day passport Macon, fast passport Middle Georgia, Bibb County passport");

    const servicesList = [
      { name: "Emergency Passport", description: "Urgent passport processing for travelers with immediate departure needs, as fast as 24 hours." },
      { name: "Passport Renewal", description: "Fast passport renewal for U.S. citizens with a passport issued within the last 15 years at age 16 or older." },
      { name: "New Passport", description: "First-time passport application assistance with step-by-step guidance through the acceptance clerk process." },
      { name: "Lost or Stolen Passport", description: "Passport replacement service for lost or stolen passports, including DS-64 form guidance." },
      { name: "Child Passport", description: "Passport services for minors under 16, with parental consent guidance and acceptance clerk support." },
      { name: "Name Change", description: "Passport correction and name change service after marriage, divorce, or legal name changes." },
      { name: "Second Passport & Damaged", description: "Second passport issuance for frequent travelers and damaged passport replacement." },
    ];

    const graph = [
      {
        "@type": "BreadcrumbList",
        itemListElement: [
          { "@type": "ListItem", position: 1, name: "Home", item: "https://fastpassportcenter.com/" },
          { "@type": "ListItem", position: 2, name: "Georgia", item: "https://fastpassportcenter.com/georgia/" },
          { "@type": "ListItem", position: 3, name: "Macon Passport Services", item: pageUrl },
        ],
      },
      {
        "@type": "LocalBusiness",
        name: "Fast Passport Center",
        description: "Fast Passport Center does not have a physical office in Macon — we serve this area remotely. Emergency and expedited passport services as fast as 24 hours for Macon and Middle Georgia travelers.",
        url: pageUrl,
        telephone: "+1-877-253-0084",
        areaServed: {
          "@type": "City",
          name: "Macon",
          containedInPlace: {
            "@type": "State",
            name: "Georgia",
          },
        },
        priceRange: "$$$",
        openingHours: ["Mo-Fr 09:00-18:00"],
        aggregateRating: {
          "@type": "AggregateRating",
          ratingValue: "4.9",
          reviewCount: "1200",
        },
      },
      ...servicesList.map((s) => ({
        "@type": "Service",
        serviceType: s.name,
        provider: {
          "@type": "LocalBusiness",
          name: "Fast Passport Center",
        },
        areaServed: {
          "@type": "City",
          name: "Macon",
        },
        description: s.description,
      })),
      {
        "@type": "FAQPage",
        mainEntity: faqs.map((faq) => ({
          "@type": "Question",
          name: faq.q,
          acceptedAnswer: {
            "@type": "Answer",
            text: faq.a,
          },
        })),
      },
    ];

    const script = document.createElement("script");
    script.type = "application/ld+json";
    script.id = "structured-data";
    document.getElementById("structured-data")?.remove();
    script.textContent = JSON.stringify({ "@context": "https://schema.org", "@graph": graph });
    document.head.appendChild(script);

    return () => {
      const existing = document.getElementById("structured-data");
      if (existing) existing.remove();
      const canonical = document.querySelector('link[rel="canonical"]');
      if (canonical) canonical.remove();
    };
  }, []);

  return (
    <div className="font-sans">
      <MaconHeroSection />
      <MaconStickyNav />
      <MaconProcessingTimeSection />
      <MaconServicesSection />
      <MaconLocalAreaSection />
      <MaconTestimonialsSection />
      <MaconAcceptanceFacilitiesSection />
      <MaconComparisonSection />
      <MaconMistakesSection />
      <MaconHowItWorksSection />
      <MaconFAQSection />
      <MaconHelpfulResourcesSection />
      <SEOCrossLinks currentRoute="/cities/macon" state="GA" />
      <MaconFinalCTA />
    </div>
  );
}